import { api } from "@/lib/api";
import type { PageResponse } from "@/types/api";
import type {
    CampaignAnalyticsDto,
    CampaignDto,
    CreateCampaignRequest,
    UpdateCampaignRequest,
} from "@/types/campaign";

export interface ListParams {
    page?: number;
    size?: number;
    sort?: string;
    q?: string;
    status?: "all" | "active" | "paused" | "archived";
    /** Advertiser filter. -1 means "IS NULL" (unassigned). */
    advertiserId?: number;
    /** Affiliate filter. -1 means "IS NULL" (unassigned). */
    affiliateId?: number;
}

function paramsOf(p: ListParams = {}): Record<string, string | number> {
    const out: Record<string, string | number> = {};
    if (p.page != null) out.page = p.page;
    if (p.size != null) out.size = p.size;
    if (p.sort) out.sort = p.sort;
    if (p.q && p.q.trim()) out.q = p.q.trim();
    if (p.status && p.status !== "all") out.status = p.status;
    if (p.advertiserId != null) out.advertiserId = p.advertiserId;
    if (p.affiliateId != null) out.affiliateId = p.affiliateId;
    return out;
}

export async function listCampaignsPaged(params: ListParams = {}): Promise<PageResponse<CampaignDto>> {
    const { data } = await api.get<PageResponse<CampaignDto>>("/admin/campaigns", { params: paramsOf(params) });
    return data;
}

export async function listCampaignsForDomain(domainId: number): Promise<CampaignDto[]> {
    const { data } = await api.get<CampaignDto[]>(`/admin/campaigns/by-domain/${domainId}`);
    return data;
}

export async function getCampaign(id: number): Promise<CampaignDto> {
    const { data } = await api.get<CampaignDto>(`/admin/campaigns/${id}`);
    return data;
}

export async function createCampaign(req: CreateCampaignRequest): Promise<CampaignDto> {
    const { data } = await api.post<CampaignDto>("/admin/campaigns", req);
    return data;
}

export async function updateCampaign(id: number, req: UpdateCampaignRequest): Promise<CampaignDto> {
    const { data } = await api.patch<CampaignDto>(`/admin/campaigns/${id}`, req);
    return data;
}

export async function deleteCampaign(id: number): Promise<void> {
    await api.delete(`/admin/campaigns/${id}`);
}

export async function getCampaignStats(id: number, range = "30d"): Promise<CampaignAnalyticsDto> {
    const { data } = await api.get<CampaignAnalyticsDto>(`/admin/campaigns/${id}/stats`, { params: { range } });
    return data;
}
